import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  StatePill,
  TextInput,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, UnauthenticatedError, apiGet, apiSend } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import { usePlatformOwner } from "../access/platform-owner.js";

/**
 * Sprint 34 (GT-COMMS-DOMAINS-FOUNDATION-01) — Platform Admin Mail
 * health/readiness page.
 *
 * Surfaces the boot-time provider plus the closed `readiness` bag
 * the API composes from env presence flags, with a clear visual
 * mapping between "no real sends" and "real sends gated; explicit
 * Owner-Go required."
 *
 * Also hosts the safe test-send drawer (default `dryRun=true`,
 * non-allowlisted recipients rejected by the backend, no scary
 * "send live email" affordance).
 *
 * Hard guarantees:
 *   - Platform-owner gated (server side AND in this UI).
 *   - Test-send form ALWAYS defaults to `dryRun: true`. Switching
 *     it off requires an explicit operator gesture and a backend
 *     allowlist match.
 *   - The page never displays env values; it reads only the
 *     readiness booleans + counts + non-secret stream id.
 *   - No raw mail-provider import.
 */

/** D-GTCC-FRAME1 — shared with the GTCC sidebar badge so both surfaces read the same cache entry. */
export const MAIL_HEALTH_QUERY_KEY = ["admin", "mail", "health"] as const;

export interface MailHealthResponse {
  readonly provider: "log-only" | "smtp" | "postmark";
  readonly readiness: {
    readonly realSendConfigured: boolean;
    readonly postmarkTokenConfigured: boolean;
    readonly smtpHostConfigured: boolean;
    readonly defaultFromConfigured: boolean;
    readonly testSendEnabled: boolean;
    readonly allowlistRecipientCount: number;
    readonly postmarkMessageStream: string;
    /**
     * Sprint 51.0 — the public web origin invitation emails embed in
     * accept-invite links. Non-secret by design. `null` means no
     * base was resolved at boot — invite emails fall back to the
     * legacy "follow the link from your admin" copy without a link.
     */
    readonly inviteAcceptUrlBase: string | null;
    readonly inviteAcceptUrlConfigured: boolean;
  };
}

export function isMailHealthResponse(value: unknown): value is MailHealthResponse {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  const readiness = candidate.readiness;
  if (
    !["log-only", "smtp", "postmark"].includes(String(candidate.provider)) ||
    !readiness ||
    typeof readiness !== "object"
  ) {
    return false;
  }
  const fields = readiness as Record<string, unknown>;
  return (
    typeof fields.realSendConfigured === "boolean" &&
    typeof fields.postmarkTokenConfigured === "boolean" &&
    typeof fields.smtpHostConfigured === "boolean" &&
    typeof fields.defaultFromConfigured === "boolean" &&
    typeof fields.testSendEnabled === "boolean" &&
    typeof fields.allowlistRecipientCount === "number" &&
    typeof fields.postmarkMessageStream === "string" &&
    (typeof fields.inviteAcceptUrlBase === "string" || fields.inviteAcceptUrlBase === null) &&
    typeof fields.inviteAcceptUrlConfigured === "boolean"
  );
}

/**
 * D-GTCC-FRAME1 — the sidebar badge count for "Mail & Deliverability".
 * Counts exactly the readiness rows this page itself renders with `tone:
 * "warn"` (default-from, test-send-enabled, invite-accept-url), so the badge
 * can never drift from what the operator sees on the page it links to.
 */
export function mailHealthWarningCount(data: MailHealthResponse): number {
  let count = 0;
  if (!data.readiness.defaultFromConfigured) count += 1;
  if (data.readiness.testSendEnabled) count += 1;
  if (!data.readiness.inviteAcceptUrlConfigured) count += 1;
  return count;
}

export function MailHealthPage() {
  const { t } = useLocale();
  const session = useSession();

  if (!usePlatformOwner(session)) {
    return (
      <Card elevation={1}>
        <EmptyState title={t("admin.mail.gate.title")} body={t("admin.mail.gate.body")} />
      </Card>
    );
  }

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h1
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
            margin: 0,
          }}
        >
          {t("admin.mail.title")}
        </h1>
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>{t("admin.mail.subtitle")}</p>
      </Stack>
      <MailHealthContent />
    </Stack>
  );
}

/**
 * Shared mail operations surface used by gtcc1 and the GTCC mono-deck.
 *
 * The surrounding shells own their platform-owner gate and page heading;
 * keeping the data and mutation path here prevents the two admin surfaces
 * from drifting on the dry-run and allowlist safety contract.
 */
export function MailHealthContent() {
  const { t } = useLocale();
  const query = useQuery<MailHealthResponse, Error>({
    queryKey: MAIL_HEALTH_QUERY_KEY,
    queryFn: ({ signal }) => apiGet<MailHealthResponse>("/v1/admin/mail/health", undefined, signal),
    staleTime: 30_000,
  });

  if (query.isLoading) return <LoadingState label={t("common.loading")} />;
  if (query.isError) {
    const isUnauth = query.error instanceof UnauthenticatedError;
    const requestId = query.error instanceof ApiError ? query.error.requestId : undefined;
    return (
      <Card elevation={1}>
        <ErrorState
          title={
            isUnauth
              ? t("admin.mail.error.session_expired_title")
              : t("admin.mail.error.load_title")
          }
          body={
            isUnauth
              ? t("admin.mail.error.session_expired_body")
              : requestId
                ? t("admin.mail.error.load_body_with_request", { requestId })
                : t("admin.mail.error.load_body")
          }
          action={
            isUnauth ? null : (
              <Button variant="secondary" onClick={() => query.refetch()}>
                {t("admin.mail.error.retry")}
              </Button>
            )
          }
        />
      </Card>
    );
  }
  if (!isMailHealthResponse(query.data)) {
    return (
      <Card elevation={1}>
        <ErrorState
          title={t("admin.mail.error.load_title")}
          body={t("admin.mail.error.load_body")}
          action={
            <Button variant="secondary" onClick={() => query.refetch()}>
              {t("admin.mail.error.retry")}
            </Button>
          }
        />
      </Card>
    );
  }
  return (
    <Stack gap={3} data-testid="admin-mail-health-content">
      <ProviderCard data={query.data} />
      <ReadinessCard data={query.data} />
      <TestSendCard data={query.data} />
    </Stack>
  );
}

function providerTone(provider: MailHealthResponse["provider"]): StatePillTone {
  switch (provider) {
    case "postmark":
      return "success";
    case "smtp":
      return "info";
    case "log-only":
    default:
      return "neutral";
  }
}

function ProviderCard({ data }: { data: MailHealthResponse }) {
  const { t } = useLocale();
  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-mail-provider-card">
        <Inline gap={2} align="center" justify="space-between" wrap>
          <strong>{t("admin.mail.provider.title")}</strong>
          <StatePill
            tone={providerTone(data.provider)}
            label={t(`admin.mail.provider.label.${data.provider}` as const)}
            dataTestId="admin-mail-provider-pill"
          />
        </Inline>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-sm)",
          }}
        >
          {t(`admin.mail.provider.description.${data.provider}` as const)}
        </span>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.mail.provider.stream", { stream: data.readiness.postmarkMessageStream })}
        </span>
      </Stack>
    </Card>
  );
}

function ReadinessCard({ data }: { data: MailHealthResponse }) {
  const { t } = useLocale();
  const items: Array<{
    key: string;
    label: string;
    value: boolean | number;
    tone: StatePillTone;
    detail: string;
  }> = [
    {
      key: "real-send-configured",
      label: t("admin.mail.readiness.real_send_configured"),
      value: data.readiness.realSendConfigured,
      tone: data.readiness.realSendConfigured ? "info" : "neutral",
      detail: data.readiness.realSendConfigured
        ? t("admin.mail.readiness.real_send_configured.detail.yes")
        : t("admin.mail.readiness.real_send_configured.detail.no"),
    },
    {
      key: "postmark-token",
      label: t("admin.mail.readiness.postmark_token"),
      value: data.readiness.postmarkTokenConfigured,
      tone: data.readiness.postmarkTokenConfigured ? "info" : "neutral",
      detail: t("admin.mail.readiness.postmark_token.detail"),
    },
    {
      key: "smtp-host",
      label: t("admin.mail.readiness.smtp_host"),
      value: data.readiness.smtpHostConfigured,
      tone: data.readiness.smtpHostConfigured ? "info" : "neutral",
      detail: t("admin.mail.readiness.smtp_host.detail"),
    },
    {
      key: "default-from",
      label: t("admin.mail.readiness.default_from"),
      value: data.readiness.defaultFromConfigured,
      tone: data.readiness.defaultFromConfigured ? "success" : "warn",
      detail: data.readiness.defaultFromConfigured
        ? t("admin.mail.readiness.default_from.detail.yes")
        : t("admin.mail.readiness.default_from.detail.no"),
    },
    {
      key: "test-send",
      label: t("admin.mail.readiness.test_send"),
      value: data.readiness.testSendEnabled,
      // test-send enabled is fine in staging but loud in prod; mark
      // it with `warn` so the operator notices when it would be
      // unexpected.
      tone: data.readiness.testSendEnabled ? "warn" : "neutral",
      detail: data.readiness.testSendEnabled
        ? t("admin.mail.readiness.test_send.detail.enabled")
        : t("admin.mail.readiness.test_send.detail.disabled"),
    },
    {
      key: "allowlist-count",
      label: t("admin.mail.readiness.allowlist_count"),
      value: data.readiness.allowlistRecipientCount,
      tone: data.readiness.allowlistRecipientCount > 0 ? "info" : "neutral",
      detail: t("admin.mail.readiness.allowlist_count.detail", {
        count: data.readiness.allowlistRecipientCount,
      }),
    },
    // Sprint 51.0 — surface the resolved invite-accept URL base so
    // the operator can see at a glance whether outbound invitation
    // emails will contain a clickable accept link. When the API
    // resolves a base, the value is rendered verbatim (non-secret —
    // it is the public web origin). When `null`, the warn tone
    // flags that invitations will fall back to the legacy "follow
    // the link from your admin" copy and no link will be embedded.
    {
      key: "invite-accept-url",
      label: t("admin.mail.readiness.invite_accept_url"),
      value: data.readiness.inviteAcceptUrlConfigured,
      tone: data.readiness.inviteAcceptUrlConfigured ? "success" : "warn",
      detail: data.readiness.inviteAcceptUrlBase
        ? t("admin.mail.readiness.invite_accept_url.detail.configured", {
            base: data.readiness.inviteAcceptUrlBase,
          })
        : t("admin.mail.readiness.invite_accept_url.detail.missing"),
    },
  ];

  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-mail-readiness-card">
        <strong>{t("admin.mail.readiness.title")}</strong>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.mail.readiness.subtitle")}
        </span>
        <ul
          style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}
          data-testid="admin-mail-readiness-list"
        >
          {items.map((item) => (
            <li
              key={item.key}
              data-testid={`admin-mail-readiness-row-${item.key}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                alignItems: "center",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--gt-sys-border)",
                background: "var(--gt-sys-surface)",
              }}
            >
              <Stack gap={0}>
                <strong style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>{item.label}</strong>
                <span
                  style={{
                    color: "var(--gt-sys-text-muted)",
                    fontSize: "var(--gt-sys-font-size-xs)",
                  }}
                >
                  {item.detail}
                </span>
              </Stack>
              <StatePill
                tone={item.tone}
                label={
                  typeof item.value === "boolean"
                    ? item.value
                      ? t("common.yes")
                      : t("common.no")
                    : String(item.value)
                }
                dataTestId={`admin-mail-readiness-pill-${item.key}`}
              />
            </li>
          ))}
        </ul>
      </Stack>
    </Card>
  );
}

interface TestSendResponse {
  readonly provider: "log-only" | "smtp" | "postmark";
  readonly messageId: string | null;
  readonly throttled: boolean;
  readonly dryRun: boolean;
}

function TestSendCard({ data }: { data: MailHealthResponse }) {
  const { t } = useLocale();
  const enabled = data.readiness.testSendEnabled;

  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  // Sprint 34 Mail Safety invariant: dryRun ALWAYS defaults to true.
  // The checkbox checked = dryRun (i.e. checked = safe). The operator
  // has to deliberately uncheck "dry run" to exercise the live
  // provider, and even then the backend allowlist gate still applies.
  const [dryRun, setDryRun] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<TestSendResponse | null>(null);

  const mutation = useMutation<TestSendResponse, Error, void>({
    mutationFn: () =>
      apiSend<TestSendResponse>("POST", "/v1/admin/mail/test-send", {
        to: recipient.trim(),
        ...(subject.trim() ? { subject: subject.trim() } : {}),
        ...(text.trim() ? { text: text.trim() } : {}),
        dryRun,
      }),
    onMutate: () => {
      setSubmitError(null);
      setResult(null);
    },
    onSuccess: (res) => setResult(res),
    onError: (err) => {
      if (err instanceof UnauthenticatedError) {
        setSubmitError(t("admin.mail.error.session_expired_body"));
        return;
      }
      if (err instanceof ApiError) {
        const requestId = err.requestId;
        setSubmitError(
          requestId
            ? t("admin.mail.test_send.error.body_with_request", {
                status: err.status,
                requestId,
              })
            : t("admin.mail.test_send.error.body", { status: err.status }),
        );
        return;
      }
      setSubmitError(err.message);
    },
  });

  const recipientValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim()),
    [recipient],
  );

  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-mail-test-send-card">
        <Inline gap={2} align="center" justify="space-between" wrap>
          <strong>{t("admin.mail.test_send.title")}</strong>
          <StatePill
            tone={enabled ? "info" : "neutral"}
            label={
              enabled
                ? t("admin.mail.test_send.status.enabled")
                : t("admin.mail.test_send.status.disabled")
            }
            dataTestId="admin-mail-test-send-status-pill"
          />
        </Inline>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-sm)",
          }}
        >
          {enabled
            ? t("admin.mail.test_send.subtitle.enabled")
            : t("admin.mail.test_send.subtitle.disabled")}
        </span>
        {!enabled ? (
          <ErrorState
            title={t("admin.mail.test_send.disabled.title")}
            body={t("admin.mail.test_send.disabled.body")}
          />
        ) : (
          <form
            data-testid="admin-mail-test-send-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!recipientValid) {
                setSubmitError(t("admin.mail.test_send.error.recipient_invalid"));
                return;
              }
              mutation.mutate();
            }}
            style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-3)" }}
          >
            <TextInput
              label={t("admin.mail.test_send.field.recipient")}
              value={recipient}
              onChange={(e) => setRecipient(e.currentTarget.value)}
              placeholder={t("admin.mail.test_send.field.recipient_placeholder")}
              type="email"
              maxLength={254}
              data-testid="admin-mail-test-send-recipient"
            />
            <TextInput
              label={t("admin.mail.test_send.field.subject")}
              value={subject}
              onChange={(e) => setSubject(e.currentTarget.value)}
              maxLength={120}
              data-testid="admin-mail-test-send-subject"
            />
            <TextInput
              label={t("admin.mail.test_send.field.text")}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              maxLength={4_000}
              data-testid="admin-mail-test-send-text"
            />
            <label
              style={{ display: "flex", alignItems: "center", gap: 8 }}
              data-testid="admin-mail-test-send-dry-run-row"
            >
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.currentTarget.checked)}
                data-testid="admin-mail-test-send-dry-run"
              />
              <span style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
                {t("admin.mail.test_send.field.dry_run")}
              </span>
            </label>
            <span
              style={{
                color: dryRun
                  ? "var(--gt-sys-text-muted)"
                  : "var(--gt-sys-warning, var(--gt-sys-text-muted))",
                fontSize: "var(--gt-sys-font-size-xs)",
              }}
              data-testid="admin-mail-test-send-mode-hint"
            >
              {dryRun
                ? t("admin.mail.test_send.field.dry_run.hint.on")
                : t("admin.mail.test_send.field.dry_run.hint.off")}
            </span>
            {submitError ? (
              <ErrorState
                title={t("admin.mail.test_send.error.title")}
                body={submitError}
                action={
                  <Button variant="secondary" onClick={() => setSubmitError(null)}>
                    {t("admin.tenants.error.dismiss")}
                  </Button>
                }
              />
            ) : null}
            {result ? (
              <Card elevation={0}>
                <Stack gap={1} data-testid="admin-mail-test-send-result">
                  <strong>{t("admin.mail.test_send.result.title")}</strong>
                  <span
                    style={{
                      color: "var(--gt-sys-text-muted)",
                      fontSize: "var(--gt-sys-font-size-xs)",
                    }}
                  >
                    {t("admin.mail.test_send.result.summary", {
                      provider: result.provider,
                      dryRun: result.dryRun ? t("common.yes") : t("common.no"),
                      throttled: result.throttled ? t("common.yes") : t("common.no"),
                    })}
                  </span>
                  {result.messageId ? (
                    <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>
                      {t("admin.mail.test_send.result.message_id", {
                        messageId: result.messageId,
                      })}
                    </code>
                  ) : null}
                </Stack>
              </Card>
            ) : null}
            <Inline gap={2} justify="end" align="center" wrap>
              <Button
                type="submit"
                variant="primary"
                disabled={mutation.isPending || !recipientValid}
                data-testid="admin-mail-test-send-submit"
              >
                {mutation.isPending
                  ? t("common.saving")
                  : dryRun
                    ? t("admin.mail.test_send.submit.dry_run")
                    : t("admin.mail.test_send.submit.live")}
              </Button>
            </Inline>
          </form>
        )}
      </Stack>
    </Card>
  );
}
